import useSWR from 'swr';
import {useCallback} from 'react';
import {buildApiUrl} from "@utils/api";

export interface IPluginReview {
    reviewer: string;
    title: string;
    content: string;
    rating: number;
    created: {
        timestamp: number;
        timezone: string;
    };
}

export interface IPluginReviewsResponse {
    success: boolean;
    data: {
        reviews: IPluginReview[];
        total: number;
        page: number;
        per_page: number;
        total_pages: number;
    };
}

const fetcher = async (url: string) => {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error('Failed to fetch reviews');
    }
    return response.json();
};

export const usePluginReviews = (pluginId: string, page: number = 1, perPage: number = 50) => {
    const reviewsUrl = pluginId ? buildApiUrl(`/plugins/${pluginId}/reviews`) : null;

    const { data, error, mutate } = useSWR<IPluginReviewsResponse>(
        reviewsUrl ? `${reviewsUrl}?page=${page}&per_page=${perPage}` : null,
        fetcher
    );

    const loadNextPage = useCallback(() => {
        if (data && data.data.page < data.data.total_pages) {
            return mutate();
        }
    }, [data, mutate]);

    return {
        reviews: data?.data.reviews ?? [],
        total: data?.data.total ?? 0,
        currentPage: data?.data.page ?? 1,
        totalPages: data?.data.total_pages ?? 1,
        perPage: data?.data.per_page ?? perPage,
        isLoading: !error && !data,
        isError: error,
        loadNextPage,
        hasNextPage: data ? data.data.page < data.data.total_pages : false,
    };
}; 
